import {use, useCallback} from "react";
import {type NavigateFunction, UNSAFE_DataRouterContext, useLocation, useNavigate} from "react-router-dom";

/**
 * Returns a predicate telling whether a router navigation is currently in flight.
 *
 * A data router runs the target's loaders BEFORE it pushes the history entry, so while a navigation
 * is pending, `useLocation()` and `window.location` still describe the page being left. Effects that
 * reconcile the URL with the store have to stand down for that window: `navigate()` aborts a pending
 * navigation, so such an effect would silently win over the navigation the app actually intended.
 *
 * Deliberately a getter to be called inside the effect rather than a rendered value: the router
 * commits its state through `startTransition`, while a Redux update renders at sync priority - so a
 * render triggered by the store still shows the navigation as idle. `router.state` is written
 * synchronously by the router and is therefore the only source that is correct at effect time.
 *
 * Outside a data router - a plain `MemoryRouter`/`BrowserRouter`, as many unit tests use - navigation
 * is synchronous and never pending, hence `false`.
 * @return predicate, stable across renders
 */
export function useNavigationPending(): () => boolean {
    const router = use(UNSAFE_DataRouterContext)?.router;
    return useCallback(() => router != null && router.state.navigation.state !== "idle", [router]);
}

/**
 * Custom hook to navigate to a new route with the current query parameters.
 *
 * Liest den Query-String bewusst aus window.location statt aus useLocation: React Router führt
 * navigate() als Transition aus, d.h. unter Last kann useLocation beim nächsten Klick noch die
 * Location VOR einer gerade committeten Navigation liefern (stale Closure) — damit ginge z.B. ein
 * frisch gesetzter Filter-Parameter (statusWIP=...) beim Tab-Wechsel verloren. window.location ist
 * dagegen immer aktuell (gleiche Begründung wie beim statusFilterParam-Read in ValuationsTable).
 * @return A function that takes a pathname and navigates to it with the current query parameters.
 */
export function useNavigateWithQuery(): (pathname: string) => void {
    const navigate = useNavigate();
    return (pathname) => {
        const search = window.location.search;

        void navigate({pathname, search});
    };
}

function isInViewport(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
}

/**
 * Navigate to a route and set the element id as hash.
 * @param focus das Element nach dem Scrollen fokussieren (für Eingabefelder)
 */
export function navigateToRouteAndHash(
    navigate: NavigateFunction,
    route: string,
    elementId: string,
    {focus = false}: {focus?: boolean} = {},
): void {
    if (!(navigate instanceof Function)) return;
    void navigate(route);
    // "smooth" wird unter WebKit oft durch ein Fokus-/Layout-Event abgebrochen, bevor der Scroll
    // ankommt (WebKit Bug 189907, #19115) - "instant" scrollt synchron gegen das aktuelle Layout;
    // isInViewport() fängt einen Re-Render ab, der die Position danach nochmal verschiebt.
    const scroll = (retriesLeft: number) => {
        const el = document.getElementById(elementId);
        if (!el) {
            if (retriesLeft > 0) setTimeout(() => scroll(retriesLeft - 1), 100);
            return;
        }
        el.scrollIntoView({behavior: "instant"});
        if (!isInViewport(el) && retriesLeft > 0) {
            setTimeout(() => scroll(retriesLeft - 1), 100);
        } else if (focus && el instanceof HTMLElement) {
            el.focus({preventScroll: true});
        }
    };
    setTimeout(() => scroll(20), 200);
}

type QueryParamSetter = (
    newValue: string | null | undefined,
    interceptHandleParams?: (params: URLSearchParams) => void,
) => void;

/**
 * Custom hook to manage query parameters in the URL.
 * @param key The key of the query parameter to manage.
 * @return The current value of the query parameter and a function to update it.
 */
export function useQueryParam(key: string): [string | null, QueryParamSetter] {
    const location = useLocation();
    const navigate = useNavigate();

    const params = new URLSearchParams(location.search);
    const value = params.get(key);

    const setValue: QueryParamSetter = (newValue, interceptHandleParams) => {
        // window.location statt useLocation-Closure — siehe useNavigateWithQuery: das Closure kann
        // unter Last stale sein und würde hier auf einen alten Pfad/Query zurücknavigieren.
        const newParams = new URLSearchParams(window.location.search);

        if (newValue === null || newValue === undefined) {
            newParams.delete(key);
        } else {
            newParams.set(key, newValue);
        }
        if (typeof interceptHandleParams === "function") {
            interceptHandleParams(newParams);
        }

        void navigate(`${window.location.pathname}?${newParams.toString()}`, {replace: true});
    };

    return [value, setValue];
}
